function primitiveText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value.toString();
  }
  if (value instanceof Date) return value.toISOString();
  return '';
}

/** Trimmed display text for a spreadsheet cell. */
export function cellText(value: unknown): string {
  return primitiveText(value).trim();
}

/**
 * Collapse AS/400 / Client Access header noise (newlines, extra spaces) so
 * "PRODUCT\\nACCESS\\nCOMPANY" and "Product Access Company" compare equal.
 */
export function normalizeHeader(value: unknown): string {
  return cellText(value)
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

/**
 * True when a qty / PO / order cell is a real non-zero. Empty, `0`, and
 * `0.00` are the clean values these reports print.
 */
export function isNonzeroNumber(value: unknown): boolean {
  const text = cellText(value);
  if (text === '') return false;
  const parsed = Number(text.replace(/,/g, ''));
  if (!Number.isNaN(parsed)) return parsed !== 0;
  return text !== '0' && text !== '0.00';
}

/** First index whose normalized header equals `name`, or -1. */
export function headerIndex(headerRow: unknown[], name: string): number {
  const wanted = normalizeHeader(name);
  return headerRow.findIndex((cell) => normalizeHeader(cell) === wanted);
}

/** Display text at the column named `header`, or empty when the header is missing. */
export function cellAt({
  row,
  headerRow,
  header,
}: {
  row: unknown[];
  headerRow: unknown[];
  header: string;
}): string {
  const index = headerIndex(headerRow, header);
  if (index === -1) return '';
  return cellText(row[index]);
}

/** Bind `cellAt` to one data row so callers pass only the header name. */
export function rowCell(
  row: unknown[],
  headerRow: unknown[],
): (header: string) => string {
  return function cell(header: string): string {
    return cellAt({ row, headerRow, header });
  };
}

/** First AOA row that contains every required header, or -1. */
export function findHeaderRow(
  rows: unknown[][],
  required: readonly string[],
): number {
  function rowHasRequiredHeaders(row: unknown[]): boolean {
    return required.every((header) => headerIndex(row, header) !== -1);
  }
  return rows.findIndex(rowHasRequiredHeaders);
}

/** The first `count` rows of an AOA sheet, used as a header fingerprint. */
export function headerFingerprint(rows: unknown[][], count = 3): Set<string> {
  const names = new Set<string>();
  for (const row of rows.slice(0, count)) {
    for (const cell of row) {
      const name = normalizeHeader(cell);
      if (name !== '') names.add(name);
    }
  }
  return names;
}

export function hasAllHeaders(
  names: Set<string>,
  required: readonly string[],
): boolean {
  return required.every((header) => names.has(normalizeHeader(header)));
}
